import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import NavBar from "../components/Navbar/NavBar";
import heroImg from "../images/bannerImage.jpg";
import aiBanner from "../images/AI_Banner.jpg";
import { motion, AnimatePresence } from "framer-motion";

const Hero = () => {
  const [offset, setOffset] = useState(0);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  const images = [heroImg, aiBanner];

  const heroContent = [
    {
      heading: "Powering Your Business with Innovative Software Solutions",
      paragraph:
        "At Gravient, our expert developers craft high-quality, reliable software tailored to your business needs. We combine innovation, performance, and precision to deliver solutions that drive growth and success.",
    },
    {
      heading: "Our Vision: A Future Powered by AI",
      paragraph:
        "At Gravient Technologies, our vision is to embrace the possibilities of artificial intelligence to redefine industries, improve decision making, and spark innovation at every level. We see AI as the foundation of tomorrow’s digital landscape, driving sustainable growth and shaping a future of endless opportunities.",
    },
  ];

  // Navbar offset
  useEffect(() => {
    const calculateOffset = () => {
      const navbar = document.getElementById("navbar");
      if (navbar) setOffset(navbar.offsetHeight);
    };

    calculateOffset();
    window.addEventListener("resize", calculateOffset);
    return () => window.removeEventListener("resize", calculateOffset);
  }, []);

  // Auto switch every 7.5s
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        handleNext();
      }, 7500);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused]);

  // Manual Navigation
  const handleNext = () => {
    setDirection(1);
    setIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Slide animation variants
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      zIndex: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
      zIndex: 0,
    }),
  };

  // Handlers for pause/resume on hold
  const handleMouseDown = () => setIsPaused(true);
  const handleMouseUp = () => setIsPaused(false);

  return (
    <>
      <NavBar />

      {/* Hero Section */}
      <div
        id="hero"
        className="relative w-full h-[85vh] flex items-center justify-center overflow-hidden"
        style={{ marginTop: `${offset}px` }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
      >
        {/* Background slider */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.img
              key={index}
              src={images[index]}
              alt="Hero background"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 1, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 container mx-auto px-4 md:px-12 lg:px-24 flex flex-col lg:flex-row items-center justify-center h-full max-w-full overflow-hidden">
          <div className="lg:w-1/2 flex flex-col justify-center text-center lg:text-left space-y-6 max-w-full">
            {/* Heading */}
            <motion.h1
              key={`heading-${index}`}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white drop-shadow-lg leading-tight max-w-full break-words"
            >
              {heroContent[index].heading}
            </motion.h1>

            {/* Paragraph */}
            <motion.div
              key={`para-${index}`}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative bg-white/10 backdrop-blur-md rounded-xl p-6 md:p-8 shadow-lg max-w-full mx-auto lg:mx-0 break-words"
            >
              <p className="text-white text-lg md:text-xl font-medium leading-relaxed drop-shadow-md">
                {heroContent[index].paragraph}
              </p>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="mt-6 flex justify-center lg:justify-start"
              >
                <Link
                  to="/contact"
                  className="bg-[#15467a] hover:bg-[#0f395f] text-white font-semibold px-8 py-3 rounded-2xl shadow-lg transition duration-300 flex items-center space-x-2"
                >
                  <span>Get Started</span>
                  <svg
                    className="w-4 h-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 
                      0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 
                      11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 
                      0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={handlePrev}
          className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/40 text-white p-3 rounded-full hover:bg-black/70 transition z-20"
        >
          ‹
        </button>
        <button
          onClick={handleNext}
          className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/40 text-white p-3 rounded-full hover:bg-black/70 transition z-20"
        >
          ›
        </button>

        {/* Dots Navigation */}
        <div className="absolute bottom-6 flex justify-center w-full space-x-3 z-20">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > index ? 1 : -1);
                setIndex(i);
              }}
              className={`w-3 h-3 rounded-full ${
                i === index ? "bg-white" : "bg-gray-400"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </>
  );
};

export default Hero;
